import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Loader2, Mail, MessageSquare, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatApiError } from '@/utils/apiError';

const SMS_LIMIT = 480;

/** Staff: fire the "Your website is the last step to Ox Approved" pitch by email or text. */
export function WebsitePitchModal({ row, channel: initial = 'email', onClose, onSent }) {
  const [channel, setChannel] = useState(initial);
  const [preview, setPreview] = useState(null);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [sms, setSms] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/staff/installer-checklist/${row.user_id}/pitch-preview`, { credentials: 'include' })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(formatApiError(d) || 'Preview failed'); return d; })
      .then((d) => { setPreview(d); setSubject(d.email.subject); setSms(d.sms.body); setTo(initial === 'sms' ? d.sms.to : d.email.to); })
      .catch((e) => toast.error(e.message));
  }, [row.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchChannel = (c) => { setChannel(c); if (preview) setTo(c === 'sms' ? preview.sms.to : preview.email.to); };

  const send = async () => {
    if (!to.trim()) { toast.error(channel === 'sms' ? 'No mobile number on file — type one in' : 'No email on file — type one in'); return; }
    setBusy(true);
    try {
      const payload = channel === 'sms' ? { channel, to: to.trim(), body: sms.trim() } : { channel, to: to.trim(), subject: subject.trim() };
      const res = await fetch(`${API_URL}/api/staff/installer-checklist/${row.user_id}/pitch`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Send failed');
      toast.success(channel === 'sms' ? `Text sent to ${to.trim()}` : `Email sent to ${to.trim()}`);
      onSent?.(d.upsell, d.pitch);
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose} data-testid="pitch-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div>
            <h2 className="text-base md:text-lg font-bold text-gray-900">Website pitch → {row.company_name || row.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">"Your website is the last step to Ox Approved" — edit the wording in Staff → Message Centre / Email Templates.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Close" data-testid="pitch-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 pt-3 flex gap-2">
          {[['email', 'Email', Mail], ['sms', 'Text (SMS)', MessageSquare]].map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => switchChannel(k)} data-testid={`pitch-channel-${k}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border-2 ${channel === k ? 'bg-[#2F5E96] text-white border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}><Icon className="w-4 h-4" /> {l}</button>
          ))}
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          {!preview ? <div className="py-8 text-center text-gray-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Building your message…</div> : (
            <>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">To
                <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder={channel === 'sms' ? '07…' : 'name@example.com'} className="mt-1 h-9" data-testid="pitch-to" />
              </label>
              {channel === 'email' ? (
                <>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">Subject
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 h-9" data-testid="pitch-subject" />
                  </label>
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Preview</div>
                  <div className="border rounded-lg p-3 bg-gray-50 max-h-[40vh] overflow-y-auto text-sm" data-testid="pitch-email-preview" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.email.html, { ADD_ATTR: ['target'] }) }} />
                </>
              ) : (
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">Message
                  <Textarea value={sms} onChange={(e) => setSms(e.target.value.slice(0, SMS_LIMIT))} rows={6} className="mt-1 text-sm" data-testid="pitch-sms-body" />
                  <span className={`block text-right text-[11px] font-normal mt-1 ${sms.length > 160 ? 'text-amber-600' : 'text-gray-400'}`}>{sms.length}/{SMS_LIMIT}{sms.length > 160 ? ` · ${Math.ceil(sms.length / 153)} SMS parts` : ''}</span>
                </label>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="pitch-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy || !preview} className="bg-[#2F5E96] hover:bg-[#25497a]" data-testid="pitch-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />} Send {channel === 'sms' ? 'text' : 'email'}
          </Button>
        </div>
      </div>
    </div>
  );
}
